import React from "react";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import { cn } from "utils";
import { Drawer } from "antd";
import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { ShowOnPcOrMobile } from "../ShowOnPcOrMobile";
import { DropDownOptionItem } from "./DropDownOptionItem";
import { useDropDown } from "./useDropDown";
import type { DropDownOption, DropDownProps } from "./types";
import Translations from "@translate/components/DropDown/DropDown";
import { I18nUtil } from "@utils/i18n";

const I18n = I18nUtil.createScoped(Translations);

export const DropDown: React.FC<DropDownProps> = (props) => {
  const {
    className,
    panelClassName,
    renderOption,
    renderTag,
    triggerDelimiter = "、",
  } = props;

  const {
    containerRef,
    open,
    triggerParts,
    sortedOptions,
    selectAllOption,
    hasSelection,
    toggleOpen,
    toggleOption,
    applySelection,
    isSelected,
    applyButtonText,
    emptyText,
    triggerAriaLabel,
    snapshot,
    getSelectedIconName,
  } = useDropDown(props);

  const triggerLabel = React.useMemo(() => {
    const summary = triggerParts.join(triggerDelimiter);
    if (summary) {
      return summary;
    }
    return triggerParts[0] ?? "";
  }, [triggerParts, triggerDelimiter]);

  const defaultTag = React.useMemo(() => {
    return (
      <div className="flex flex-1 items-center gap-1">
        <OspIcons
          name="p_filter"
          size={16}
          className="shrink-0 text-content_secondary"
        />
        <span
          className="body_s truncate text-content_secondary"
          title={triggerLabel}
        >
          {triggerLabel}
        </span>
      </div>
    );
  }, [triggerLabel]);

  const renderOptionContent = React.useCallback(
    (option: DropDownOption) => {
      if (renderOption) {
        return renderOption(option, snapshot);
      }

      return (
        <DropDownOptionItem
          option={option}
          selected={isSelected(option.value)}
          selectedIconName={getSelectedIconName(option)}
          onSelect={toggleOption}
        />
      );
    },
    [getSelectedIconName, isSelected, renderOption, snapshot, toggleOption],
  );

  return (
    <div
      ref={containerRef}
      className={cn("relative inline-flex max-w-[200px] z-30", className)}
    >
      <button
        data-openreplay-label={`Drop Down Button | ${renderTag ? renderTag(snapshot) : defaultTag}`}
        id="btn-drop-down"
        type="button"
        className={cn(
          "flex max-w-[200px] items-center gap-1 rounded-[4px] bg-background_white_overlay5 px-2 py-0.5 text-left transition-colors",
          "hover:bg-background_white_overlay10 focus:outline-none focus-visible:ring-2 focus-visible:ring-background_brand_overlay100",
        )}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={triggerAriaLabel}
        onClick={toggleOpen}
        data-tracking-scope="dropdown-trigger"
      >
        {renderTag ? renderTag(snapshot) : defaultTag}
      </button>
      <ShowOnPcOrMobile showMode="pc">
        {open ? (
          <div
            role="listbox"
            aria-multiselectable="true"
            className={cn(
              "absolute top-[calc(100%+8px)] z-20 w-[200px] rounded-2 border border-border_white_transparent10 bg-background_solid_tertiary shadow-[0px_12px_24px_rgba(0,0,0,0.45)]",
              panelClassName,
            )}
            data-tracking-scope="dropdown-panel"
          >
            <div className="flex max-h-[400px] flex-col p-2">
              {selectAllOption ? (
                <div
                  className=""
                  data-testid="dropdown-select-all"
                  data-tracking-scope="dropdown-select-all"
                >
                  {renderOptionContent(selectAllOption)}
                </div>
              ) : null}
              <div className="flex max-h-[352px] flex-col overflow-y-auto">
                {sortedOptions.length === 0 ? (
                  <div className="py-6 text-center body_s text-content_secondary">
                    {emptyText}
                  </div>
                ) : (
                  sortedOptions.map((option) => (
                    <React.Fragment key={option.value}>
                      {renderOptionContent(option)}
                    </React.Fragment>
                  ))
                )}
              </div>
            </div>
            <div className="px-2 pb-2 pt-1">
              <button
                aria-label="Drop Down"
                data-openreplay-label={`Drop Down Button | ${applyButtonText}`}
                id="btn-drop-down-2"
                type="button"
                className={cn(
                  "w-full button xs_button tonal_button button_s text-content_primary",
                  !hasSelection && "cursor-not-allowed opacity-40",
                )}
                disabled={!hasSelection}
                aria-disabled={!hasSelection}
                onClick={() => {
                  if (!hasSelection) {
                    return;
                  }
                  applySelection();
                }}
                data-tracking-scope="dropdown-apply-button"
              >
                {applyButtonText}
              </button>
            </div>
          </div>
        ) : null}
      </ShowOnPcOrMobile>
      <ShowOnPcOrMobile showMode="mobile">
        <Drawer
          open={open}
          onClose={toggleOpen}
          placement="bottom"
          height={454}
          footer={false}
          className="!bg-background_solid_secondary"
          closeIcon={false}
          zIndex={999}
          styles={{
            body: {
              padding: "0",
            },
          }}
        >
          <div className="p-4 flex justify-between">
            <span className="headline_regular_s text-content_primary">
              {I18n.t("Filter")}
            </span>
            <Icon
              data-openreplay-label="close"
              name="close"
              size={20}
              color={ospThemes.colors.content_tertiary}
              className="button button_icon"
              onClick={toggleOpen}
            />
          </div>
          <div className="flex max-h-[320px] flex-col px-4">
            {selectAllOption ? (
              <div
                className=""
                data-testid="dropdown-select-all"
                data-tracking-scope="dropdown-select-all"
              >
                {renderOptionContent(selectAllOption)}
              </div>
            ) : null}
            <div className="flex max-h-[280px] flex-col overflow-y-auto no-scrollbar">
              {sortedOptions.length === 0 ? (
                <div className="py-6 text-center body_s text-content_secondary">
                  {emptyText}
                </div>
              ) : (
                sortedOptions.map((option) => (
                  <React.Fragment key={option.value}>
                    {renderOptionContent(option)}
                  </React.Fragment>
                ))
              )}
            </div>
          </div>
          <div className="p-4">
            <button
              aria-label="Drop Down"
              data-openreplay-label={`Drop Down Button | ${applyButtonText}`}
              id="btn-drop-down-3"
              type="button"
              className={cn(
                "w-full button md_button tonal_button button_s text-content_primary",
                !hasSelection && "cursor-not-allowed opacity-40",
              )}
              disabled={!hasSelection}
              aria-disabled={!hasSelection}
              onClick={() => {
                if (!hasSelection) {
                  return;
                }
                applySelection();
              }}
              data-tracking-scope="dropdown-apply-button-mobile"
              data-testid="dropdown-apply-button-mobile"
            >
              {applyButtonText}
            </button>
          </div>
        </Drawer>
      </ShowOnPcOrMobile>
    </div>
  );
};
